import { useMemo } from 'react';

import { useQuery } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import dpiaRequestsApi from 'config/api/dpiaRequests/dpiaRequests';
import type { DpiaHistoryList } from 'config/api/dpiaRequests/dpiaRequests.types';
import type { DpiaDetailResponse } from 'config/api/dpias/dpias.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import {
  catchHttpStatuses,
  HTTP_UNAVAILABLE_STATUSES,
  isUnavailableQueryResult,
  unwrapAvailableQueryData,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';
import { resolveDpiaTrigger } from 'pages/ProcessingActivities/_shared/resolveDpiaTrigger';

import { resolveDpiaBannerMeta, resolveSourceDpiaRequestId } from './dpiaBannerDisplay.helpers';
import { findCatalogProcessingActivity, findWorkspaceAssetByApaId, mergeDpiaEditorContext } from './dpiaEditorContext.helpers';

type Params = {
  envelope?: DpiaDetailResponse | null;
  organizationId?: number;
  useOrganizationAssetRopa?: boolean;
};

const useDpiaEditorContext = ({ envelope, organizationId, useOrganizationAssetRopa = false }: Params) => {
  const baseContext = envelope?.context;
  const dpia = envelope?.dpia;
  const assetProcessingActivityId = dpia?.assetProcessingActivityId ?? 0;
  const dpiaId = dpia?.id ?? 0;

  const dpiaHistoryQuery = useQuery(
    [QUERY_KEYS.DPIA_HISTORY, organizationId, assetProcessingActivityId, 'dpia-editor-context'],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await dpiaRequestsApi.getDpiaHistory(organizationId as number, assetProcessingActivityId);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && assetProcessingActivityId && dpiaId),
      retry: false,
    },
  );

  const dpiaHistory = unwrapAvailableQueryData<DpiaHistoryList>(dpiaHistoryQuery.data);
  const sourceDpiaRequestId = useMemo(
    () => resolveSourceDpiaRequestId(dpia?.sourceDpiaRequestId, dpiaHistory, dpiaId),
    [dpia?.sourceDpiaRequestId, dpiaHistory, dpiaId],
  );

  const sourceRequestQuery = useQuery(
    [QUERY_KEYS.DPIA_REQUEST_GET, organizationId, sourceDpiaRequestId, 'dpia-editor-context'],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await dpiaRequestsApi.getRequest(organizationId as number, sourceDpiaRequestId as number);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && sourceDpiaRequestId),
      retry: false,
    },
  );

  const apaDetailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, assetProcessingActivityId, 'dpia-editor-context'],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await assetRopaApi.getProcessingActivity(organizationId as number, assetProcessingActivityId);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && assetProcessingActivityId),
      retry: false,
    },
  );

  const assetRopaListQuery = useQuery(
    [useOrganizationAssetRopa ? QUERY_KEYS.ASSET_ROPA_ORG : QUERY_KEYS.ASSET_ROPA_MINE, organizationId, 'dpia-editor-context'],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = useOrganizationAssetRopa
          ? await assetRopaApi.getOrganizationWorkspace(organizationId as number)
          : await assetRopaApi.getMine(organizationId as number);

        return data.assets;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && assetProcessingActivityId),
      retry: false,
    },
  );

  const sourceRequestEnvelope = unwrapAvailableQueryData(sourceRequestQuery.data);
  const apaDetail = unwrapAvailableQueryData(apaDetailQuery.data);
  const assetRopaAssets = unwrapAvailableQueryData(assetRopaListQuery.data) ?? [];
  const assetRopaAsset = useMemo(
    () => findWorkspaceAssetByApaId(assetRopaAssets, assetProcessingActivityId),
    [assetProcessingActivityId, assetRopaAssets],
  );
  const processingActivityId = apaDetail?.processingActivity.id ?? 0;
  const shouldLoadCatalog =
    Boolean(assetRopaAsset?.assetRopaId && processingActivityId) &&
    !sourceRequestEnvelope?.context &&
    (baseContext?.dataSubjectCategories.length ?? 0) === 0 &&
    (baseContext?.personalDataCategories.length ?? 0) === 0;

  const catalogActivitiesQuery = useQuery(
    [
      QUERY_KEYS.ASSET_ROPA_AVAILABLE_PROCESSING_ACTIVITIES,
      organizationId,
      assetRopaAsset?.assetRopaId,
      processingActivityId,
      'dpia-editor-context',
    ],
    async () => {
      const { data } = await assetRopaApi.getAvailableProcessingActivities(organizationId as number, assetRopaAsset?.assetRopaId as number);
      return data.results;
    },
    {
      enabled: Boolean(organizationId && assetRopaAsset?.assetRopaId && shouldLoadCatalog),
      retry: false,
    },
  );

  const catalogActivity = useMemo(
    () => findCatalogProcessingActivity(catalogActivitiesQuery.data ?? [], processingActivityId),
    [catalogActivitiesQuery.data, processingActivityId],
  );

  const context = useMemo(() => {
    if (!baseContext) {
      return undefined;
    }

    return mergeDpiaEditorContext({
      apaDetail,
      assetProcessingActivityId,
      assetRopaAsset,
      base: baseContext,
      catalogActivity,
      sourceRequest: sourceRequestEnvelope,
    });
  }, [apaDetail, assetProcessingActivityId, assetRopaAsset, baseContext, catalogActivity, sourceRequestEnvelope]);

  const sourceTrigger = resolveDpiaTrigger(context?.sourceRequest, sourceRequestEnvelope?.request);
  const bannerMeta = useMemo(() => {
    if (!dpia || !context) {
      return undefined;
    }

    return resolveDpiaBannerMeta({
      context,
      detail: dpia,
      history: dpiaHistory,
      sourceRequest: sourceRequestEnvelope,
    });
  }, [context, dpia, dpiaHistory, sourceRequestEnvelope]);
  const isContextLoading =
    dpiaHistoryQuery.isLoading ||
    sourceRequestQuery.isLoading ||
    apaDetailQuery.isLoading ||
    assetRopaListQuery.isLoading ||
    catalogActivitiesQuery.isLoading;
  const isContextUnavailable =
    isUnavailableQueryResult(dpiaHistoryQuery.data) ||
    isUnavailableQueryResult(sourceRequestQuery.data) ||
    isUnavailableQueryResult(apaDetailQuery.data) ||
    isUnavailableQueryResult(assetRopaListQuery.data) ||
    isUnavailableQueryResult(catalogActivitiesQuery.data);

  return {
    bannerMeta,
    context,
    isContextLoading,
    isContextUnavailable,
    operationalOwnerMembershipId: assetRopaAsset?.operationalOwnerMembershipId ?? null,
    operationalOwnerName: assetRopaAsset?.operationalOwnerName ?? null,
    sourceTrigger,
  };
};

export default useDpiaEditorContext;
